///
/// EarthBrowser
///
/// Created by Matt Giger on 11/28/2025
/// Copyright (c) 2025 Geodata Labs
///

import { LoggingInterface, ConsoleLogger } from "@stovokor/utils/logging"
import { ShaderSourceDefinition } from "./shader/ShaderSourceCode"
import { ShaderProgram } from "./shader/ShaderProgram"
import { PaneModel } from "./uipanes/Pane"
import { Texture } from "./shader/Texture"
import { GestureSystem } from "./gestures/GestureSystem"
import { FocusManager } from "./gestures/FocusManager"
import { GLState } from "./shader/GLState"
import { VertexArrayCache } from "./shader/VertexArrayCache"
import { time } from "./math/time"
import { requestRender } from "./engine/RenderRequest"

export interface WebGLContextOptions {
	alpha?: boolean
	depth?: boolean
	stencil?: boolean
	antialias?: boolean
	premultipliedAlpha?: boolean
	preserveDrawingBuffer?: boolean
	powerPreference?: 'default' | 'high-performance' | 'low-power'
}

/**
 * StovokorContext encapsulates a WebGL rendering context and all its associated resources.
 * This allows multiple independent WebGL contexts to coexist without sharing state.
 *
 * The context must be explicitly passed to any class that needs GL access.
 * Do not create GL-dependent objects in constructors - defer to init methods that receive the context.
 */
export class StovokorContext {
	public readonly gl: WebGL2RenderingContext
	public readonly canvas: HTMLCanvasElement
	public readonly logger: LoggingInterface = new ConsoleLogger()

	/**
	 * Redundant-state filter for gl. Engine code changes program, vertex
	 * array, texture, capability, depth-mask and blend state through this
	 * rather than gl directly so consecutive draws sharing state skip the
	 * driver calls. Invalidated at the start of every frame.
	 */
	public readonly glState: GLState

	/**
	 * Unified gesture handling and focus management
	 *
	 * Handles:
	 * - DOM event handling (pointer, touch, wheel)
	 * - Scene-graph hit testing
	 * - Gesture arena management
	 * - Focus state (which node receives zoom events)
	 * - Event routing (zoom to focused, hover to closest hit)
	 * - Commands to focused node
	 */
	public readonly gestures: GestureSystem

	/**
	 * Focus manager for gesture system
	 * Manages focus state and transitions between gesture nodes
	 * Handles:
	 * - Focus state (which node receives zoom events)
	 * - Focus transitions (animated camera movement when focus changes)
	 * - Focus management (requesting and releasing focus)
	 * - Focus change events (notifying listeners when focus changes)
	 * - Frame updates (updating focus state every frame)
	 */
	public readonly focusManager: FocusManager

	/**
	 * Utility instances
	 */
	public readonly paneModel: PaneModel
	public readonly blankTexture: Texture
	/** Vertex arrays shared between renderers drawing the same geometry with the same program */
	public readonly vertexArrays: VertexArrayCache

	/**
	 * Device pixels per CSS pixel of the drawing buffer. The engine keeps all
	 * logical coordinates (viewport sizes, camera projections, pane frames,
	 * gesture positions, shader `viewport` uniforms) in CSS pixels; only the
	 * canvas backing store, gl.viewport/scissor, point sizes and pane canvas
	 * rasterization are multiplied by this. Set by Stovokor on resize.
	 */
	public pixelRatio: number = 1

	/**
	 * Upper bound on pixelRatio. 3x phones render 9x the fragments of 1x for
	 * little visible gain over 2x, so cap there by default; the app can lower
	 * it further under memory pressure or raise it for captures.
	 */
	public get maxPixelRatio(): number {
		return this._maxPixelRatio
	}
	public set maxPixelRatio(value: number) {
		this._maxPixelRatio = value
		this.canvasResized = true	// re-measure on the next tick
	}
	private _maxPixelRatio: number = 2

	/** Wall-clock time of the current frame, set by Stovokor once per tick */
	public frameTime: time = new time()

	/**
	 * Set by whoever learns the canvas may have changed size or pixel ratio
	 * (GestureSystem's ResizeObserver and window resize listener, the
	 * engine's device-pixel-ratio media query); Stovokor then measures the
	 * canvas on its next tick instead of forcing a layout every frame
	 */
	public canvasResized: boolean = true

	/** Ask the engine to draw a frame on the next tick (see Stovokor.idleFrameRate) */
	public requestRender(): void {
		requestRender()
	}

	// Context-scoped resource caches (previously global singletons)
	public textureCache = new Map<string, any>()
	public textureCubeCache = new Map<string, any>()
	private shaderCache = new Map<string, ShaderProgram>()
	private extensionCache = new Map<string, boolean>()

	constructor(canvas: HTMLCanvasElement | null, options?: WebGLContextOptions) {
		if(!(canvas instanceof HTMLCanvasElement)) {
			throw new Error(`Element is not a canvas element`)
		}

		this.canvas = canvas

		const context = canvas.getContext('webgl2', options)
		if (!context || !(context instanceof WebGL2RenderingContext)) {
			throw new Error('Unable to create WebGL2 context')
		}

		this.gl = context
		this.glState = new GLState(context)
		this.vertexArrays = new VertexArrayCache(this)

		this.gestures = new GestureSystem(this)
		this.focusManager = new FocusManager(this)

		this.paneModel = new PaneModel(this)
		this.blankTexture = new Texture(this)
		this.blankTexture.initialize(1, 1)
	}

	/**
	 * Clean up all context resources
	 */
	public destroy(): void {
		this.gestures.destroy()
		this.paneModel.destroy()
		this.blankTexture.destroy()

		this.shaderCache.forEach((value, _key) => value.destroy())
		this.shaderCache.clear()
		this.textureCache.clear()
		this.textureCubeCache.clear()
	}

	/**
	 * Check for (and enable) a WebGL extension, caching the result.
	 * Note that getExtension also enables the extension's behavior
	 * (e.g. OES_texture_float_linear makes float textures filterable)
	 */
	public hasExtension(name: string): boolean {
		let value = this.extensionCache.get(name)
		if(value === undefined) {
			value = this.gl.getExtension(name) !== null
			this.extensionCache.set(name, value)
		}
		return value
	}

	public get viewportWidth(): number {
		return this.canvas.width
	}

	public get viewportHeight(): number {
		return this.canvas.height
	}

	public getCachedShader(source: ShaderSourceDefinition): ShaderProgram {
		const value = this.shaderCache.get(source.identifier)
		if(value) {
			return value
		}

		const shader = new ShaderProgram(this, source)
		this.shaderCache.set(source.identifier, shader)
		return shader
	}

	/** Compile state of every cached shader program, for diagnostics */
	public shaderDiagnostics(): { id: string, sourceId: string, defines: Record<string, string>, available: boolean, compiling: boolean, errors: string[] }[] {
		return Array.from(this.shaderCache.entries()).map(([id, shader]) => ({
			id,
			sourceId: shader.source.sourceId,
			defines: shader.source.defines,
			available: shader.available,
			compiling: shader.compiling,
			errors: shader.errors.map(error => `${error.line}: ${error.message}`)
		}))
	}

	public getShaderSourceString(_sourceId: string): string {
		throw new Error("Not implemented.Implement in subclass")
	}
}
